import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { type IntlService } from 'ember-intl';
import { t } from 'ember-intl';
import * as v from 'valibot';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';

import authRegisterPayrollIdSchema from 'tio-common/validation-schema/forms/auth-register-payroll-id-form';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import formatSubdomainRedirectUrl from 'tio-common/utils/format-subdomain-redirect-url';
import handlePreregisterError from 'tio-employee/utils/preregister-error';
import { parseHostname } from 'tio-common/utils/hostname';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';
import Button from 'tio-ui/components/button';
import Form from 'tio-ui/components/form';
import Input from 'tio-ui/components/input';
import type { FormResult } from 'tio-ui/components/form';
import type { NotificationsService } from 'tio-ui/components/notifications';
import ENV from 'tio-employee/config/environment';
import type PreRegisterService from '../../../services/pre-register.ts';

import LoginHere from '../login-here';

interface AuthRegisterPayrollIdFormComponentSignature {
  Args: {
    settings: CompanySettingModel;
    email: string;
  };
}

interface PayrollIdFormData {
  email: string;
  payrollId: string;
  lastName: string;
}

export default class AuthRegisterPayrollIdForm extends Component<AuthRegisterPayrollIdFormComponentSignature> {
  @service declare intl: IntlService;
  @service declare preRegister: PreRegisterService;
  @service declare router: RouterService;
  @service declare notifications: NotificationsService;

  @tracked formData: PayrollIdFormData;

  constructor(owner: Owner, args: AuthRegisterPayrollIdFormComponentSignature['Args']) {
    super(owner, args);
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromParams = decodeURIComponent(urlParams.get('email') || '');
    this.formData = {
      email: args.email || emailFromParams,
      payrollId: '',
      lastName: '',
    };
  }

  get company() {
    return this.preRegister.sparseCompany;
  }

  get registerPageCopy() {
    return (
      this.args.settings.payrollCustomText ||
      this.intl.t('login.register_page_default', { companyName: this.companyName })
    );
  }

  get companyName() {
    // @ts-expect-error: seems like a bug
    return this.args.settings.companyName || '';
  }

  validate = (data: PayrollIdFormData) => {
    const r = v.safeParse(authRegisterPayrollIdSchema, data);
    return r.success ? {} : (v.flatten(r.issues).nested ?? {});
  };

  handleFormChange = (result: FormResult<PayrollIdFormData>) => {
    this.formData = result.data;
  };

  registerTask = dropTask(async () => {
    // Captured before the await: the inputs stay editable while the request is in flight.
    const { email, payrollId, lastName } = this.formData;
    const response = await fetch(`${ENV.apiHost}/preregister`, {
      method: 'POST',
      body: JSON.stringify({
        email,
        company_id: this.company?.id || null,
        payroll_id: payrollId,
        last_name: lastName,
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ENV.apiKey,
        'Time-Zone': timeZone(),
        'X-Request-Id': generateRequestId(),
      },
    });

    if (response.ok) {
      const res = await response.json();
      if (res.sub_domain) {
        const redirectUrl = formatSubdomainRedirectUrl({
          subDomain: res.sub_domain,
          domain: parseHostname(window.location.hostname).domain,
          email,
          route: 'register',
        });

        window.location.href = redirectUrl;
      } else {
        this.router.transitionTo('register.confirmation');
      }
    } else {
      await handlePreregisterError(response, {
        email,
        intl: this.intl,
        notifications: this.notifications,
        router: this.router,
      });
    }
  });

  handleSubmit = () => {
    this.registerTask.perform();
  };

  <template>
    <Form
      @data={{this.formData}}
      @validate={{this.validate}}
      @onChange={{this.handleFormChange}}
      @onSubmit={{this.handleSubmit}}
      data-test-payroll-form
      as |form|
    >
      <h2 class="text-lg mb-6 text-center">{{this.registerPageCopy}}</h2>
      <div class="mb-10">
        {{#let (form.field "payrollId") as |f|}}
          <Input
            @label="Payroll ID"
            @value={{f.value}}
            @onChange={{f.onChange}}
            @onBlur={{f.onBlur}}
            @errors={{f.errors}}
            data-test-payroll-id-field
          />
        {{/let}}
      </div>
      <div class="mb-10">
        {{#let (form.field "lastName") as |f|}}
          <Input
            @label="Last Name"
            @value={{f.value}}
            @onChange={{f.onChange}}
            @onBlur={{f.onBlur}}
            @errors={{f.errors}}
            data-test-last-name-field
          />
        {{/let}}
      </div>
      <div class="mb-10">
        {{#let (form.field "email") as |f|}}
          <Input
            @label={{t "login.email"}}
            @description="Please provide your personal email address. This is where you will receive your invite."
            @type="email"
            @value={{f.value}}
            @onChange={{f.onChange}}
            @onBlur={{f.onBlur}}
            @errors={{f.errors}}
            data-test-email-field
          />
        {{/let}}
      </div>

      <div class="tio-stack-center">
        <Button
          @intent="primary"
          @appearance="outlined"
          type="submit"
          class="w-36 mb-10"
          data-test-submit-button
        >
          {{t "common.submit"}}
        </Button>
      </div>
      <LoginHere />
    </Form>
  </template>
}
